import { useReducer } from 'react'

export enum Status {
  idle = 'idle',
  pending = 'pending',
  success = 'success',
  error = 'error',
}

export type State = {
  status: Status
  error: Record<string, unknown> | undefined
  [key: string]: unknown
}

/** Status reducer. */
const stateReducer = (
  state: State,
  action: {
    type: Status
    error?: Record<string, unknown> | undefined
    payload?: Record<string, unknown> | undefined
  },
) => {
  switch (action.type) {
    case Status.pending:
    case Status.idle:
      return { status: action.type, error: undefined }
    case Status.success:
      return {
        status: action.type,
        error: undefined,
        ...(action.payload || {}),
      }
    case Status.error:
      return { status: action.type, error: action.error }
    default:
      throw new Error('Undefined state type')
  }
}

/** Generic operation status hook. */
const useStatus = (): {
  state: State
  dispatchError: (error: Record<string, unknown>) => void
  dispatchSuccess: (payload?: Record<string, unknown> | undefined) => void
  dispatchIdle: () => void
  dispatchPending: () => void
} => {
  const [state, dispatch] = useReducer(stateReducer, {
    status: Status.idle,
    error: undefined,
  })

  return {
    state,
    dispatchError: (error: Record<string, unknown>) =>
      dispatch({ type: Status.error, error }),
    dispatchSuccess: (payload?: Record<string, unknown> | undefined) =>
      dispatch({ type: Status.success, payload }),
    dispatchIdle: () => dispatch({ type: Status.idle }),
    dispatchPending: () => dispatch({ type: Status.pending }),
  }
}

export default useStatus
